"use client";

/**
 * UserLayoutPrefsContext
 * Provides per-user nav visibility/order and home section visibility/order.
 * Loads from Firestore on sign-in; falls back to role-based defaults.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type PropsWithChildren,
} from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { applyFooterStyle, type FooterStyle } from '@/lib/footer-style';
import { applyButtonOutline, cleanOutlineWidth } from '@/lib/button-outline';
import { applyTextShadow, cleanTextShadow } from '@/lib/text-shadow';
import { isNewMenuMember, MENU_VERSION } from '@/lib/menu-config';
import {
  getUserLayoutPrefs,
  saveUserLayoutPrefs,
  getDefaultNavPrefs,
  getNewMemberNavPrefs,
  resolveNewMemberNav,
  getDefaultHomeSectionPrefs,
  DEFAULT_HERO_CONTENT,
  defaultHeaderBoardPrefs,
  type MenuSide, type PlayerSize,
  type UserHeaderBoardPrefs,
  type UserNavPref,
  type UserHomeSectionPref,
  type UserHeroContentPrefs,
} from '@/services/user-layout-prefs';

// The menu side also lives on this device so the layout is right from the
// first paint (before Firestore answers). Only an EXPLICIT choice is stored
// (MENU_SIDE_CHOICE_KEY); without one, EVERYONE gets the mirrored layout —
// menu far right, search far left, chat and play/player toggle swapped
// (visitors since 2026-10-31; signed-in people too since 2026-11-01, Gordon:
// "put the menu on the right after you log in as well, as default"). The old
// key held the applied default on every device, so only its "right" counts
// as a choice. layout.tsx's pre-paint script mirrors this logic.
const MENU_SIDE_CHOICE_KEY = 'bb-menu-side-choice';
const LEGACY_MENU_SIDE_KEY = 'bb-menu-side';

function applyMenuSide(side: MenuSide) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.menuSide = side;
}

function rememberMenuSide(side: MenuSide) {
  try { localStorage.setItem(MENU_SIDE_CHOICE_KEY, side); } catch { /* private mode */ }
}

function chosenMenuSide(): MenuSide | null {
  try {
    const v = localStorage.getItem(MENU_SIDE_CHOICE_KEY);
    if (v === 'left' || v === 'right') return v;
    return localStorage.getItem(LEGACY_MENU_SIDE_KEY) === 'right' ? 'right' : null;
  } catch { return null; }
}

// Same default signed in or out — kept as a function in case they diverge again.
const defaultMenuSide = (_signedIn: boolean): MenuSide => 'right';

/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */

interface UserLayoutPrefsContextType {
  navPrefs: UserNavPref[];
  homeSectionPrefs: UserHomeSectionPref[];
  loading: boolean;
  updateNavPrefs: (prefs: UserNavPref[]) => Promise<void>;
  updateHomeSectionPrefs: (prefs: UserHomeSectionPref[]) => Promise<void>;
  heroContentPrefs: UserHeroContentPrefs;
  updateHeroContentPrefs: (prefs: UserHeroContentPrefs) => Promise<void>;
  menuSide: MenuSide;
  updateMenuSide: (side: MenuSide) => Promise<void>;
  /** The user's header message board — their own choice, else the default for their account type. */
  headerBoardPrefs: UserHeaderBoardPrefs;
  updateHeaderBoardPrefs: (prefs: UserHeaderBoardPrefs) => Promise<void>;
  /** The person's own footer player background (null = default). */
  footerStyle: FooterStyle | null;
  updateFooterStyle: (style: FooterStyle | null) => Promise<void>;
  /** coπ's voice ('' = the device's own). */
  copiVoice: string;
  updateCopiVoice: (voice: string) => Promise<void>;
  /** Desktop player bar height (compact = header height, the default). */
  playerSize: PlayerSize;
  updatePlayerSize: (size: PlayerSize) => Promise<void>;
  /** Their own button outline width, or null = BB's default. */
  buttonOutline: string | null;
  updateButtonOutline: (width: string | null) => Promise<void>;
  /** Text/icon shadow amount ('0'…'3'); null = BB's default. */
  textShadow: string | null;
  updateTextShadow: (level: string | null) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  /** Only the menu back to its default (the per-account-type one, or Super
   *  Admin's when set) — home sections stay as they are. */
  resetNavToDefault: () => Promise<void>;
}

const UserLayoutPrefsContext = createContext<UserLayoutPrefsContextType | null>(null);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function UserLayoutPrefsProvider({ children }: PropsWithChildren) {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [navPrefs, setNavPrefs] = useState<UserNavPref[]>(() =>
    getDefaultNavPrefs()
  );
  const [homeSectionPrefs, setHomeSectionPrefs] = useState<UserHomeSectionPref[]>(() =>
    getDefaultHomeSectionPrefs()
  );
  const [loading, setLoading] = useState(true);
  const [heroContentPrefs, setHeroContentPrefs] = useState<UserHeroContentPrefs>(DEFAULT_HERO_CONTENT);
  const [menuSide, setMenuSide] = useState<MenuSide>('right');
  const [savedHeaderBoard, setSavedHeaderBoard] = useState<UserHeaderBoardPrefs | null>(null);
  const [footerStyle, setFooterStyle] = useState<FooterStyle | null>(null);
  const [copiVoice, setCopiVoice] = useState('');
  const [playerSize, setPlayerSize] = useState<PlayerSize>('compact');
  // globals.css reads html[data-player-size] (desktop only; phones keep 56px).
  useEffect(() => { document.documentElement.setAttribute('data-player-size', playerSize); }, [playerSize]);
  const [buttonOutline, setButtonOutline] = useState<string | null>(null);
  const [textShadow, setTextShadow] = useState<string | null>(null);
  const headerBoardPrefs = useMemo<UserHeaderBoardPrefs>(() => {
    const base = defaultHeaderBoardPrefs(userProfile?.accountType);
    return savedHeaderBoard
      ? { ...savedHeaderBoard, size: savedHeaderBoard.size || base.size, sources: { ...base.sources, ...(savedHeaderBoard.sources || {}) } }
      : base;
  }, [savedHeaderBoard, userProfile?.accountType]);

  // Device choice first (instant), else the default for visitor / signed in;
  // the account's saved choice once it loads.
  useEffect(() => {
    let signedIn = false;
    try { signedIn = localStorage.getItem('bb-signed-in') === '1'; } catch { /* private mode */ }
    const side = chosenMenuSide() ?? defaultMenuSide(signedIn);
    setMenuSide(side);
    applyMenuSide(side);
  }, []);
  // Signing in or out without a choice on this device: that state's default.
  useEffect(() => {
    if (authLoading || chosenMenuSide()) return;
    const side = defaultMenuSide(!!user?.uid);
    setMenuSide(side);
    applyMenuSide(side);
  }, [user?.uid, authLoading]);

  // Load EXACTLY ONCE per signed-in uid. Previously this effect also re-ran when
  // userProfile.accountType resolved (a moment after sign-in) — if the user had
  // already toggled a menu item in that window, the re-fetch overwrote their
  // optimistic edit with the older stored value, so the toggle "didn't remember".
  const loadedUidRef = useRef<string | null>(null);
  // Edits made while the initial Firestore load is still in flight. Persisting
  // them immediately would save a DEFAULTS-based array and clobber the user's
  // stored customizations; instead we hold them here and replay them on top of
  // the loaded prefs, then persist once.
  const pendingNavEditsRef = useRef<Map<string, boolean> | null>(null);
  const loadingRef = useRef(true);
  // 2 = this member's menu follows the per-account-type defaults (lib/menu-
  // config), so saves keep that mark. Undefined = an older menu.
  const menuVersionRef = useRef<number | undefined>(undefined);
  // True while the menu is a computed default (nothing saved yet) — it then
  // follows the account type once that loads.
  const navComputedRef = useRef(false);
  const isNewMemberRef = useRef(false);
  const withVersion = useCallback(
    (nav: UserNavPref[]) => (menuVersionRef.current === MENU_VERSION ? { nav, menuVersion: MENU_VERSION } : { nav }),
    [],
  );

  useEffect(() => {
    if (!user?.uid) {
      setNavPrefs(getDefaultNavPrefs(userProfile?.accountType));
      setHomeSectionPrefs(getDefaultHomeSectionPrefs());
      setCopiVoice('');
      setPlayerSize('compact');
      setLoading(false);
      loadingRef.current = false;
      loadedUidRef.current = null;
      menuVersionRef.current = undefined;
      navComputedRef.current = false;
      return;
    }

    // Already loaded for this user — never re-fetch/clobber in-session edits.
    if (loadedUidRef.current === user.uid) return;
    loadedUidRef.current = user.uid;

    // Only a switch to another account discards this load. The profile's account
    // type arriving a moment later re-runs this effect, which returns early above —
    // cancelling on that left members on the default menu instead of their saved
    // one (Gordon's menu showed only Profile/Settings/Collab, 2026-11-03).
    const forUid = user.uid;
    const stale = () => loadedUidRef.current !== forUid;
    setLoading(true);
    loadingRef.current = true;

    // A member whose account is newer than Phase A and who has no saved menu
    // gets the default for their account type; everyone else keeps exactly
    // the menu they had (saved, or today's computed default).
    const isNew = isNewMenuMember(user.metadata?.creationTime);
    isNewMemberRef.current = isNew;
    getUserLayoutPrefs(user.uid, userProfile?.accountType)
      .then(prefs => {
        if (stale()) return;
        const newDefault = isNew && !prefs?.navSaved;
        let nav: UserNavPref[];
        if (newDefault) nav = getNewMemberNavPrefs(userProfile?.accountType);
        else if (prefs) nav = prefs.nav;
        else nav = getDefaultNavPrefs(userProfile?.accountType);
        navComputedRef.current = newDefault || !prefs;
        menuVersionRef.current = newDefault || prefs?.menuVersion === MENU_VERSION ? MENU_VERSION : undefined;
        // Replay toggles made during the load window on top of the stored base.
        const pending = pendingNavEditsRef.current;
        if (pending && pending.size > 0) {
          nav = nav.map(p => (pending.has(p.href) ? { ...p, visible: pending.get(p.href)! } : p));
          pendingNavEditsRef.current = null;
          navComputedRef.current = false;
          void saveUserLayoutPrefs(user.uid, withVersion(nav)).catch(() => {});
        }
        if (prefs?.navMigrated && !newDefault) void saveUserLayoutPrefs(user.uid, { nav, navDefaultsVersion: 1 }).catch(() => {});
        setNavPrefs(nav);
        setHomeSectionPrefs(prefs ? prefs.homeSections : getDefaultHomeSectionPrefs());
        setHeroContentPrefs({ ...DEFAULT_HERO_CONTENT, ...(prefs?.heroContent || {}) });
        setSavedHeaderBoard(prefs?.headerBoard || null);
        setFooterStyle(prefs?.footerStyle || null);
        setCopiVoice(typeof prefs?.copiVoice === 'string' ? prefs.copiVoice : '');
        setPlayerSize(prefs?.playerSize === 'medium' || prefs?.playerSize === 'tall' ? prefs.playerSize : 'compact');
        applyFooterStyle('user', prefs?.footerStyle);
        const outline = cleanOutlineWidth(prefs?.buttonOutline);
        setButtonOutline(outline);
        applyButtonOutline('user', outline);
        const shadow = cleanTextShadow(prefs?.textShadow);
        setTextShadow(shadow);
        applyTextShadow(shadow);
        if (prefs?.menuSide === 'left' || prefs?.menuSide === 'right') {
          setMenuSide(prefs.menuSide);
          applyMenuSide(prefs.menuSide);
          rememberMenuSide(prefs.menuSide);
        }
        setLoading(false);
        loadingRef.current = false;
      })
      .catch(err => {
        console.error('[layout-prefs] load failed:', err);
        if (!stale()) {
          setNavPrefs(isNew ? getNewMemberNavPrefs(userProfile?.accountType) : getDefaultNavPrefs(userProfile?.accountType));
          navComputedRef.current = true;
          menuVersionRef.current = isNew ? MENU_VERSION : undefined;
          setHomeSectionPrefs(getDefaultHomeSectionPrefs());
          setLoading(false);
          loadingRef.current = false;
        }
      });
  }, [user?.uid, userProfile?.accountType, withVersion]);

  // A computed (unsaved) menu follows the account type once the profile loads
  // — it used to stay on the first guess until the next page load.
  useEffect(() => {
    if (!user?.uid || loading || !navComputedRef.current) return;
    setNavPrefs(isNewMemberRef.current ? getNewMemberNavPrefs(userProfile?.accountType) : getDefaultNavPrefs(userProfile?.accountType));
  }, [user?.uid, userProfile?.accountType, loading]);

  /* Persist nav prefs + update local state (writes ONLY nav — merge-safe) */
  const updateNavPrefs = useCallback(
    async (prefs: UserNavPref[]) => {
      setNavPrefs(prev => {
        // During the initial load window: apply visually, record the diff for
        // replay, and DON'T write yet (a write now would clobber stored prefs
        // with a defaults-based array).
        if (loadingRef.current) {
          const pending = pendingNavEditsRef.current || new Map<string, boolean>();
          const prevMap = new Map(prev.map(p => [p.href, p.visible]));
          prefs.forEach(p => {
            if (prevMap.get(p.href) !== p.visible) pending.set(p.href, p.visible);
          });
          pendingNavEditsRef.current = pending;
        }
        return prefs;
      });
      if (!loadingRef.current) navComputedRef.current = false;
      if (user?.uid && !loadingRef.current) {
        try {
          await saveUserLayoutPrefs(user.uid, withVersion(prefs));
        } catch (err) {
          console.error('[layout-prefs] save nav failed (toggle will not persist):', err);
          // Silent failure = "didn't remember" — tell the user instead.
          toast({
            title: "Couldn't save your menu settings",
            description: 'Check your connection and flip the toggle again.',
            variant: 'destructive',
          });
        }
      }
    },
    [user?.uid, toast, withVersion]
  );

  /* Persist home section prefs + update local state (writes ONLY homeSections) */
  const updateHomeSectionPrefs = useCallback(
    async (prefs: UserHomeSectionPref[]) => {
      setHomeSectionPrefs(prefs);
      if (user?.uid) {
        try {
          await saveUserLayoutPrefs(user.uid, { homeSections: prefs });
        } catch (err) {
          console.error('[layout-prefs] save homeSections failed:', err);
          toast({
            title: "Couldn't save your home layout",
            description: 'Check your connection and try again.',
            variant: 'destructive',
          });
        }
      }
    },
    [user?.uid, toast]
  );

  /* What the hero shows for this user (merge-safe write of heroContent only) */
  const updateHeroContentPrefs = useCallback(
    async (prefs: UserHeroContentPrefs) => {
      setHeroContentPrefs(prefs);
      if (user?.uid) {
        try {
          await saveUserLayoutPrefs(user.uid, { heroContent: prefs });
        } catch (err) {
          console.error('[layout-prefs] save heroContent failed:', err);
        }
      }
    },
    [user?.uid]
  );

  /* Left- or right-handed layout: applied instantly, saved on the device and
     (when signed in) on the account so it follows the user to other devices. */
  const updateMenuSide = useCallback(
    async (side: MenuSide) => {
      setMenuSide(side);
      applyMenuSide(side);
      rememberMenuSide(side);
      if (user?.uid) {
        try {
          await saveUserLayoutPrefs(user.uid, { menuSide: side });
        } catch (err) {
          console.error('[layout-prefs] save menuSide failed:', err);
        }
      }
    },
    [user?.uid]
  );

  /* Footer player background — applied at once, saved on the account once
     the sliders settle (a drag fires dozens of changes). */
  const footerSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateFooterStyle = useCallback(
    async (style: FooterStyle | null) => {
      setFooterStyle(style);
      applyFooterStyle('user', style);
      if (!user?.uid) return;
      const uid = user.uid;
      if (footerSaveTimer.current) clearTimeout(footerSaveTimer.current);
      footerSaveTimer.current = setTimeout(() => {
        saveUserLayoutPrefs(uid, { footerStyle: style ?? {} })
          .catch(err => console.error('[layout-prefs] save footerStyle failed:', err));
      }, 600);
    },
    [user?.uid]
  );

  const updateCopiVoice = useCallback(
    async (voice: string) => {
      setCopiVoice(voice);
      if (!user?.uid) return;
      try {
        await saveUserLayoutPrefs(user.uid, { copiVoice: voice });
      } catch (err) {
        console.error('[layout-prefs] save copiVoice failed:', err);
      }
    },
    [user?.uid]
  );

  /* Button outline thickness — one value for every button (null = BB's default). */
  const updateButtonOutline = useCallback(
    async (width: string | null) => {
      const w = cleanOutlineWidth(width);
      setButtonOutline(w);
      applyButtonOutline('user', w);
      if (!user?.uid) return;
      try {
        await saveUserLayoutPrefs(user.uid, { buttonOutline: w ?? '' });
      } catch (err) {
        console.error('[layout-prefs] save buttonOutline failed:', err);
      }
    },
    [user?.uid]
  );

  /* Text/icon shadow amount (null = BB's default). */
  const updateTextShadow = useCallback(
    async (level: string | null) => {
      const l = cleanTextShadow(level);
      setTextShadow(l);
      applyTextShadow(l);
      if (!user?.uid) return;
      try {
        await saveUserLayoutPrefs(user.uid, { textShadow: l ?? '' });
      } catch (err) {
        console.error('[layout-prefs] save textShadow failed:', err);
      }
    },
    [user?.uid]
  );

  const updatePlayerSize = useCallback(
    async (size: PlayerSize) => {
      setPlayerSize(size);
      if (!user?.uid) return;
      try { await saveUserLayoutPrefs(user.uid, { playerSize: size }); } catch (err) { console.error('[layout-prefs] save playerSize failed:', err); }
    },
    [user?.uid]
  );

  /* Header message board: size + sources (merge-safe write of headerBoard only) */
  const updateHeaderBoardPrefs = useCallback(
    async (prefs: UserHeaderBoardPrefs) => {
      setSavedHeaderBoard(prefs);
      if (user?.uid) {
        try {
          await saveUserLayoutPrefs(user.uid, { headerBoard: prefs });
        } catch (err) {
          console.error('[layout-prefs] save headerBoard failed:', err);
        }
      }
    },
    [user?.uid]
  );

  /* Reset to the defaults: the menu a new member of this account type starts
     with (Super Admin's default when set) — saved as a menuVersion-2 menu. */
  const defaultNavForReset = useCallback(async () => {
    try { return await resolveNewMemberNav(userProfile?.accountType); }
    catch { return getNewMemberNavPrefs(userProfile?.accountType); }
  }, [userProfile?.accountType]);

  const resetNavToDefault = useCallback(async () => {
    const defaultNav = await defaultNavForReset();
    setNavPrefs(defaultNav);
    menuVersionRef.current = MENU_VERSION;
    navComputedRef.current = false;
    if (user?.uid) await saveUserLayoutPrefs(user.uid, { nav: defaultNav, menuVersion: MENU_VERSION });
  }, [user?.uid, defaultNavForReset]);

  const resetToDefaults = useCallback(async () => {
    const defaultNav = await defaultNavForReset();
    const defaultSections = getDefaultHomeSectionPrefs();
    setNavPrefs(defaultNav);
    setHomeSectionPrefs(defaultSections);
    menuVersionRef.current = MENU_VERSION;
    navComputedRef.current = false;
    if (user?.uid) {
      await saveUserLayoutPrefs(user.uid, {
        nav: defaultNav,
        menuVersion: MENU_VERSION,
        homeSections: defaultSections,
      });
    }
  }, [user?.uid, defaultNavForReset]);

  return (
    <UserLayoutPrefsContext.Provider
      value={{
        navPrefs,
        homeSectionPrefs,
        loading,
        updateNavPrefs,
        updateHomeSectionPrefs,
        heroContentPrefs,
        updateHeroContentPrefs,
        menuSide,
        updateMenuSide,
        headerBoardPrefs,
        updateHeaderBoardPrefs,
        footerStyle,
        updateFooterStyle,
        copiVoice,
        updateCopiVoice,
        playerSize,
        updatePlayerSize,
        buttonOutline,
        updateButtonOutline,
        textShadow,
        updateTextShadow,
        resetToDefaults,
        resetNavToDefault,
      }}
    >
      {children}
    </UserLayoutPrefsContext.Provider>
  );
}

/** The user's menu side, or the default ('right') outside the provider. */
export function useMenuSide(): MenuSide {
  return useContext(UserLayoutPrefsContext)?.menuSide ?? 'right';
}

/** The header board prefs, or null outside the provider. */
export function useHeaderBoardPrefs(): UserHeaderBoardPrefs | null {
  return useContext(UserLayoutPrefsContext)?.headerBoardPrefs ?? null;
}

/** Like useUserLayoutPrefs, but null outside the provider instead of throwing. */
export function useOptionalUserLayoutPrefs() {
  return useContext(UserLayoutPrefsContext);
}

export function useUserLayoutPrefs() {
  const ctx = useContext(UserLayoutPrefsContext);
  if (!ctx)
    throw new Error('useUserLayoutPrefs must be used within UserLayoutPrefsProvider');
  return ctx;
}
